import React, { useEffect, useState } from "react";
import { getDatabase, ref, get, remove } from "firebase/database";
import { useNavigate } from "react-router-dom";
import UElogo2 from "../assets/logo_horizontal.png";
import { Trash2 } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx"
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import {
  fetchAdminLevels,
  getAdminLevel,
  isAllowedDomain,
  getUserType,
} from "../utils/userAccess";
import AppTopBar from "../components/AppTopBar";
import AppFooter from "../components/AppFooter";

function incrementSlot(timeStr) {
  const [h, m] = timeStr.split(":").map(Number);
  const date = new Date(0, 0, 0, h, m);
  date.setMinutes(date.getMinutes() + 30);
  return date.toTimeString().slice(0, 5);
}

function mergeTimes(times) {
  const sorted = [...times].sort();
  const merged = [];
  let i = 0;
  while (i < sorted.length) {
    const start = sorted[i];
    let end = incrementSlot(start);
    let j = i + 1;
    while (j < sorted.length && sorted[j] === end) {
      end = incrementSlot(end);
      j++;
    }
    merged.push({ start, end });
    i = j;
  }
  return merged;
}

export default function AdminReservationsPage() {
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [months, setMonths] = useState([]);
  const [users, setUsers] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [days, setDays] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedDay, setSelectedDay] = useState("All");
  const [selectedUser, setSelectedUser] = useState("All");
  const [selectedBuilding, setSelectedBuilding] = useState("All");

  const auth = getAuth();
  const navigate = useNavigate();
  const [adminLevel, setAdminLevel] = useState(0);
  const [showMenu, setShowMenu] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchReservations = async () => {
      const db = getDatabase();
      const dbRef = ref(db, "reservations");
      try {
        const snapshot = await get(dbRef);
        const allData = snapshot.val();
        const grouped = {};
        const userEmails = new Set();
        const userMap = {};

        for (const building in allData) {
          const rooms = allData[building];
          for (const room in rooms) {
            const dates = rooms[room];
            for (const date in dates) {
              const times = dates[date];
              for (const time in times) {
                const reservation = times[time];
                if (reservation.status === "Reserved") {
                  const key = `${building}||${room}||${date}||${reservation.email}`;
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(time);
                  userEmails.add(reservation.email);
                  userMap[`${key}||${time}`] = reservation.user;
                }
              }
            }
          }
        }

        const mergedReservations = [];

        for (const key in grouped) {
          const [building, room, date, email] = key.split("||");
          const mergedTimes = mergeTimes(grouped[key]);
          mergedTimes.forEach(({ start, end }) => {
            const userKey = `${key}||${start}`;
            mergedReservations.push({
              building,
              room,
              date,
              start,
              end,
              email,
              user: userMap[userKey] || "Unknown",
            });
          });
        }

        mergedReservations.sort((a, b) =>
          `${a.date} ${a.start}`.localeCompare(`${b.date} ${b.start}`),
        );

        setReservations(mergedReservations);
        setFilteredReservations(mergedReservations);
        setRooms([
          "All",
          ...[...new Set(mergedReservations.map((r) => r.room))].sort(),
        ]);

        setMonths([
          "All",
          ...[...new Set(mergedReservations.map((r) => r.date.slice(0, 7)))].sort(),
        ]);

        setDays([
          "All",
          ...[...new Set(mergedReservations.map((r) => r.date))].sort(),
        ]);

        setUsers([
          "All",
          ...[...new Set(userEmails)].sort(),
        ]);

        setBuildings([
          "All",
          ...[...new Set(mergedReservations.map((r) => r.building))].sort(),
        ]);
      } catch (error) {
        console.error("Error fetching reservations:", error);
      }
    };

    fetchReservations();
  }, []);

  useEffect(() => {
    // Start from all reservations
    let filtered = reservations;

    // Apply filters in any order
    if (selectedMonth !== "All") {
      filtered = filtered.filter((r) => r.date.startsWith(selectedMonth));
    }

    if (selectedDay !== "All") {
      filtered = filtered.filter((r) => r.date === selectedDay);
    }

    if (selectedRoom !== "All") {
      filtered = filtered.filter((r) => r.room === selectedRoom);
    }

    if (selectedUser !== "All") {
      filtered = filtered.filter((r) => r.email === selectedUser);
    }

    if (selectedBuilding !== "All") {
      filtered = filtered.filter((r) => r.building === selectedBuilding);
    }

    // Update what is displayed in the reservation list
    setFilteredReservations(filtered);

    // Dynamically populate dropdowns based on filtered data
    const nextDays = [
      "All",
      ...[...new Set(filtered.map((r) => r.date))].sort(),
    ];
    setDays(nextDays);

    const nextRooms = [
      "All",
      ...[...new Set(filtered.map((r) => r.room))].sort(),
    ];
    setRooms(nextRooms);

    const nextUsers = [
      "All",
      ...[...new Set(filtered.map((r) => r.email))].sort(),
    ];
    setUsers(nextUsers);

    const nextBuildings = [
      "All",
      ...[...new Set(filtered.map((r) => r.building))].sort(),
    ];
    setBuildings(nextBuildings);
  }, [
      selectedRoom,
      selectedMonth,
      selectedDay,
      selectedUser,
      selectedBuilding,
      reservations,
    ]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser || !isAllowedDomain(currentUser.email)) {
        alert("Access restricted to @uevora.pt or @alunos.uevora.pt emails.");
        setUser(null);
        setAdminLevel(0);
        signOut(auth);
        navigate("/");
        return;
      }

      setUser(currentUser);
      const adminMap = await fetchAdminLevels();
      const level = getAdminLevel(currentUser.email, adminMap);
      setAdminLevel(level);
    });

    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.menu-dropdown')) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCancel = async (reservation) => {
  const db = getDatabase();
  let time = reservation.start;

  while (time < reservation.end) {
    const path = `reservations/${reservation.building}/${reservation.room}/${reservation.date}/${time}`;
    await remove(ref(db, path));
    time = incrementSlot(time);
  }

  setReservations((prev) =>
    prev.filter(
      (r) =>
        !(
          r.building === reservation.building &&
          r.room === reservation.room &&
          r.date === reservation.date &&
          r.start === reservation.start
        ),
    ),
  );
};

  const formatDateDMY = (isoYmd) => {
    if (!isoYmd) return "";
    const p = isoYmd.split("-");
    if (p.length === 3) return `${p[2]}-${p[1]}-${p[0]}`; // fast path for "YYYY-MM-DD"
    const d = new Date(isoYmd);
    if (Number.isNaN(d)) return isoYmd;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const exportXLSX = () => {
    const headers = ["Date", "Classroom", "Time", "User", "Email"];

    const data = filteredReservations.map((r) => [
      formatDateDMY(r.date),            // <-- was r.date
      r.room,
      `${r.start} - ${r.end}`,
      r.user,
      r.email,
    ]);

    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reservations");
    XLSX.writeFile(workbook, "reservations.xlsx");
  };


  const exportPDF = () => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    const pageWidth = doc.internal.pageSize.width;
    const totalPagesExp = "{total_pages_count_string}";

    // Title and Logo
    doc.setFontSize(12);
    doc.text("Reservations list", 10, 20);
    const img = new Image();
    img.src = UElogo2;
    doc.addImage(img, "PNG", pageWidth - 80, 10, 70, 15);

    // Table
    autoTable(doc, {
      startY: 30,
      head: [["Date", "Classroom", "Time", "User", "Email"]],
      body: filteredReservations.map((r) => [
        formatDateDMY(r.date),
        r.room,
        `${r.start} - ${r.end}`,
        r.user,
        r.email,
      ]),
      // ⬇️ Text size controls
      styles: { fontSize: 9 },          // base for all cells
      headStyles: { fontSize: 7, fillColor: [55, 94, 151], textColor: 255, halign: "left" },     // header row
      bodyStyles: { fontSize: 7 },      // body rows (optional if same as styles)
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageSize = doc.internal.pageSize;
        const pageHeight = pageSize.height;
        const pageWidth = pageSize.width;
        const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
        const pageStr = `${pageNumber}/${pageCount}`;

        const exportUser =
          auth.currentUser?.displayName || auth.currentUser?.email || "Unknown";
        const exportTime = new Date().toLocaleString("en-GB");

        doc.setFontSize(8);
        doc.text(
          `Exported by: ${exportUser} on ${exportTime}`,
          10,
          pageHeight - 10,
        );
        doc.text(pageStr, pageWidth - 15, pageHeight - 10);
      },
    });

    const pdfBlob = doc.output("blob");
    const blobUrl = URL.createObjectURL(pdfBlob);
    window.open(blobUrl, "_blank");

    //doc.save("reservations.pdf");
  };

  const handlePrint = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;

    const img = new Image();
    img.src = UElogo2;

    img.onload = () => {
      // Header: title and logo
      doc.setFontSize(12);
      doc.text("Reservations list", 10, 20);
      doc.addImage(img, "PNG", pageWidth - 50, 10, 40, 15);

      // Table
      autoTable(doc, {
        startY: 30,
        head: [["Date", "Classroom", "Time", "User", "Email"]],
        body: filteredReservations.map((r) => [
          formatDateDMY(r.date),
          r.room,
          `${r.start} - ${r.end}`,
          r.user,
          r.email,
        ]),
        didDrawPage: (data) => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          const pageStr = `${pageNumber}/${pageCount}`;

          const exportUser =
            auth.currentUser?.displayName ||
              auth.currentUser?.email ||
              "Unknown";
          const exportTime = new Date().toLocaleString("en-GB");

          doc.setFontSize(8);
          doc.text(
            `Exported by: ${exportUser} on ${exportTime}`,
            10,
            pageHeight - 10,
          );
          doc.text(pageStr, pageWidth - 15, pageHeight - 10);
        },
      });

      // Create a blob and print via iframe
      const pdfBlob = doc.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);

      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.src = blobUrl;
      document.body.appendChild(iframe);

      iframe.onload = () => {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      };
    };
  };


  const handleSignOut = () => signOut(auth).then(() => navigate("/"));
  const goToBuildings = () => navigate("/buildings");
  const goToMyReservations = () => {
    navigate("/my-reservations");
  };
  const goToContact = () => navigate("/contact");
  const goToTerms = () => navigate("/terms-of-use");

  const buildingDisplayNames = {
    buildingA: "Mateus d'Aranda",
    buildingB: "Complexo dos Leões",
    buildingC: "Biblioteca Jorge Araújo",
  };

  return (
    <div className="flex flex-col p-6 pt-28 items-center bg-gray-800 text-gray-100 text-center w-full min-h-screen relative z-0">
      <AppTopBar
        onExportXLSX={exportXLSX}
        onExportPDF={exportPDF}
        AllReservationsMenu={false}
      />
      <h1 className="w-full mb-8 text-left px-6 text-xl font-bold">
        All Reservations
      </h1>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div>
          <label className="block mb-1">Filter by Month</label>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="p-2 rounded bg-gray-500 text-black"
          >
            {months.map((m) => (
              <option key={m} value={m}>
                {m === "All"
                  ? "All"
                  : new Date(`${m}-01`).toLocaleDateString("en-GB", {
                    year: "numeric",
                    month: "long",
                  })}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1">Filter by Day</label>
          <select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
            className="p-2 rounded bg-gray-500 text-black"
          >
            {days.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1">Filter by Classroom</label>
          <select
            value={selectedRoom}
            onChange={(e) => setSelectedRoom(e.target.value)}
            className="p-2 rounded bg-gray-500 text-black"
          >
            {rooms.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1">Filter by User</label>
          <select
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            className="p-2 rounded bg-gray-500 text-black"
          >
            {users.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block mb-1">Filter by Building</label>
          <select
            value={selectedBuilding}
            onChange={(e) => setSelectedBuilding(e.target.value)}
            className="p-2 rounded bg-gray-500 text-black"
          >
            {buildings.map((b) => (
              <option key={b} value={b}>
                {b === "All" ? "All" : (buildingDisplayNames[b] || b)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="w-full flex justify-center">
        <div className="grid grid-cols-[0.75fr_1.5fr_1fr_1.5fr_1.5fr_0.2fr] gap-4 w-full max-w-5xl mb-2 text-sm sm:text-base font-semibold text-gray-300">
          <span className="text-left">Date</span>
          <span className="text-left">Classroom</span>
          <span className="text-left">Time</span>
          <span className="text-left">Name</span>
          <span className="text-left">Email</span>
        </div>
      </div>

      {filteredReservations.length === 0 ? (
        <p className="text-gray-300">No reservations found.</p>
      ) : (
          <ul className="space-y-2 w-full max-w-5xl">
            {filteredReservations.map((r, index) => (
              <li
                key={index}
                className="bg-gray-700 p-2 rounded shadow-md grid grid-cols-[0.75fr_1.5fr_1fr_1.5fr_1.5fr_0.2fr] items-center gap-4 text-sm sm:text-base"
              >
                <span className="text-left">
                  {new Date(r.date).toLocaleDateString("en-GB")}
                </span>
                <span className="text-left">{r.room}</span>
                <span className="text-left">
                  {r.start} - {r.end}
                </span>
                <span className="text-left">{r.user}</span>
                <span className="text-left">{r.email}</span>
                <button
                  onClick={() => handleCancel(r)}
                  className="bg-gray-800 text-red-500 px-2 py-1 text-sm w-fit shadow-md rounded"
                >
                  <Trash2 className="w-5 h-5 text-red-500" />
                </button>
              </li>
            ))}
          </ul>
        )}

      <AppFooter />
    </div>
  );
}

